import React, { useEffect, useRef, useState } from 'react';

interface EncryptedTextProps {
  text: string;
  /** Class applied to characters while still scrambled */
  encryptedClassName?: string;
  /** Class applied to characters once revealed */
  revealedClassName?: string;
  /** Delay in ms between revealing each character */
  revealDelayMs?: number;
  /** Delay in ms between re-scrambling unrevealed characters */
  flipDelayMs?: number;
  /** Characters to use for scrambling */
  charset?: string;
  /** Whether to trigger the animation on mount (true) or on hover (false) */
  animateOnMount?: boolean;
  /** Additional class for the wrapping span */
  className?: string;
}

const DEFAULT_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

const EncryptedText: React.FC<EncryptedTextProps> = ({
  text,
  encryptedClassName = 'text-neutral-500',
  revealedClassName = '',
  revealDelayMs = 50,
  flipDelayMs = 30,
  charset = DEFAULT_CHARSET,
  animateOnMount = true,
  className = '',
}) => {
  // Each character slot: revealed = true means show real char, false = scrambled
  const [revealed, setRevealed] = useState<boolean[]>(Array(text.length).fill(false));
  const [scrambled, setScrambled] = useState<string[]>(() =>
    Array.from({ length: text.length }, () =>
      text[0] === ' ' ? ' ' : charset[Math.floor(Math.random() * charset.length)]
    )
  );
  const hasStarted = useRef(false);
  const flipIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const revealTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const startAnimation = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    // Continuously scramble unrevealed characters
    flipIntervalRef.current = setInterval(() => {
      setScrambled(prev => {
        const next = [...prev];
        setRevealed(rev => {
          rev.forEach((isRevealed, i) => {
            if (!isRevealed && text[i] !== ' ') {
              next[i] = charset[Math.floor(Math.random() * charset.length)];
            }
          });
          return rev;
        });
        return next;
      });
    }, flipDelayMs);

    // Reveal characters one by one
    Array.from({ length: text.length }).forEach((_, i) => {
      const t = setTimeout(() => {
        setRevealed(prev => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
        setScrambled(prev => {
          const next = [...prev];
          next[i] = text[i];
          return next;
        });

        // Once all revealed, stop scrambling
        if (i === text.length - 1) {
          if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
        }
      }, revealDelayMs * (i + 1));
      revealTimeoutsRef.current.push(t);
    });
  };

  useEffect(() => {
    if (animateOnMount) {
      startAnimation();
    }
    return () => {
      if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
      revealTimeoutsRef.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateOnMount]);

  // Reset and re-animate when text changes
  useEffect(() => {
    hasStarted.current = false;
    if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
    revealTimeoutsRef.current.forEach(clearTimeout);
    revealTimeoutsRef.current = [];
    setRevealed(Array(text.length).fill(false));
    setScrambled(
      Array.from({ length: text.length }, (_, i) =>
        text[i] === ' ' ? ' ' : charset[Math.floor(Math.random() * charset.length)]
      )
    );
    if (animateOnMount) {
      // Small delay so state has settled
      const t = setTimeout(() => startAnimation(), 10);
      revealTimeoutsRef.current.push(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleMouseEnter = () => {
    if (!animateOnMount) {
      // Reset and replay on hover
      if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
      revealTimeoutsRef.current.forEach(clearTimeout);
      revealTimeoutsRef.current = [];
      hasStarted.current = false;
      setRevealed(Array(text.length).fill(false));
      setScrambled(
        Array.from({ length: text.length }, (_, i) =>
          text[i] === ' ' ? ' ' : charset[Math.floor(Math.random() * charset.length)]
        )
      );
      setTimeout(() => startAnimation(), 10);
    }
  };

  return (
    <span className={className} onMouseEnter={handleMouseEnter} style={{ display: 'inline-block' }}>
      {scrambled.map((char, i) => (
        <span
          key={i}
          className={revealed[i] ? revealedClassName : encryptedClassName}
          style={{ display: 'inline-block', whiteSpace: 'pre' }}
        >
          {char}
        </span>
      ))}
    </span>
  );
};

export { EncryptedText };
export default EncryptedText;
